import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Menu, Zap } from "lucide-react";
import { DemoBadge } from "@/components/common";
import { http } from "@/lib/api";
import { fmtUsd, fmtPct } from "@/lib/format";
import { useFetch } from "@/lib/api";

export default function TopBar({ onMenu }) {
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const { data: market } = useFetch("/market/overview", []);
  const m = market?.data;

  useEffect(() => {
    if (!q || q.length < 2) {
      setResults([]);
      return;
    }
    const id = setTimeout(() => {
      http.get(`/search?q=${encodeURIComponent(q)}`).then((r) => {
        setResults(r.data.data || []);
        setOpen(true);
      });
    }, 200);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const h = (e) => boxRef.current && !boxRef.current.contains(e.target) && setOpen(false);
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const go = (route) => {
    setQ("");
    setOpen(false);
    setResults([]);
    nav(route);
  };

  const typeColor = {
    TOKEN: "text-amber-400",
    WALLET: "text-cyan-400",
    DEVELOPER: "text-rose-400",
    NARRATIVE: "text-emerald-400",
  };

  return (
    <header className="glass sticky top-0 z-30 flex items-center gap-3 px-4 py-2.5">
      <button
        onClick={onMenu}
        data-testid="menu-toggle"
        className="rounded-md p-1.5 text-slate-400 hover:bg-slate-800 lg:hidden"
      >
        <Menu className="h-5 w-5" />
      </button>

      <div ref={boxRef} className="relative w-full max-w-xl">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
        <input
          data-testid="universal-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => results.length && setOpen(true)}
          placeholder="Search token, symbol, contract, wallet, developer, narrative…"
          className="w-full rounded-lg border border-slate-800 bg-panel py-2 pl-9 pr-3 text-sm text-slate-200 outline-none placeholder:text-slate-600 focus:border-amber-600/50"
        />
        {open && results.length > 0 && (
          <div className="absolute mt-2 max-h-96 w-full overflow-auto rounded-lg border border-slate-700 bg-panel shadow-2xl">
            {results.map((r, i) => (
              <button
                key={i}
                data-testid={`search-result-${i}`}
                onClick={() => go(r.route)}
                className="flex w-full items-center justify-between gap-3 border-b border-slate-800/60 px-3 py-2.5 text-left hover:bg-slate-800/50"
              >
                <div>
                  <div className="text-sm font-semibold text-slate-100">{r.label}</div>
                  <div className="text-[11px] text-slate-500">{r.sub}</div>
                </div>
                <span className={`font-mono text-[10px] font-bold ${typeColor[r.type]}`}>{r.type}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="ml-auto flex items-center gap-3">
        {m && (
          <>
            <div className="hidden items-center gap-1.5 md:flex">
              <span className="font-mono text-[10px] uppercase text-slate-500">SOL</span>
              <span className="font-mono text-sm font-semibold text-slate-100">{fmtUsd(m.sol_price)}</span>
              <span className={`font-mono text-xs ${m.sol_change_24h >= 0 ? "num-pos" : "num-neg"}`}>
                {fmtPct(m.sol_change_24h)}
              </span>
            </div>
            <div className="hidden items-center gap-1.5 lg:flex">
              <Zap className="h-3.5 w-3.5 text-amber-400" />
              <span className="font-mono text-xs text-slate-300">{m.tps} TPS</span>
            </div>
          </>
        )}
        <DemoBadge />
      </div>
    </header>
  );
}
